import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import {
  Table, Button, Tag, Space, Popconfirm, Tooltip, Statistic,
  Modal, Alert, Input, InputNumber, Progress, Tabs, Select,
} from "antd";
import {
  CreditCardOutlined, PlusOutlined, DeleteOutlined, CheckCircleOutlined,
  StarOutlined, DownloadOutlined, EyeOutlined, TeamOutlined, PhoneOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchCurrentSubscription,
  fetchPaymentMethods,
  removePaymentMethod,
  setDefaultPaymentMethod,
  cancelSubscription,
  fetchInvoices,
  attachPaymentMethod,
  fetchAgentQuota,
  previewAgentSeats,
  updateAgentSeats,
  validateCoupon,
  clearAppliedCoupon,
} from "../../../core/data/redux/slices/BillingSlice";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";
import { fetchDIDTransactions } from "../../../core/data/redux/slices/didlogicSlice";

const stripePromise = loadStripe("pk_test_51JM78KBtOBT8b78eKkjaaXWTEBvsBvmV1VYV3kaRXVgjCYNVLUPK7MNPQEpHgdihSUOtPEfG8WPsVqoHgBBsev2600RynHqIML");

const CardBrandIcon = ({ brand }) => {
  const icons = { visa: "💳", mastercard: "💳", amex: "💳", discover: "💳" };
  return <span style={{ fontSize: 18 }}>{icons[brand] || "💳"}</span>;
};

export default function CompanyBilling() {
  const dispatch = useDispatch();
  const BILLING_TTL = 5 * 60 * 1000; // 5 minutes
  const {
    subscription, planStatus, trialEndsAt,
    subscriptionLastFetched,
    paymentMethods: rawPaymentMethods, defaultPaymentMethodId, paymentMethodsLoading, actionLoading,
    paymentMethodsLastFetched,
    invoices: rawInvoices, invoicesTotal, invoicesLoading, billingSummary,
    agentQuota, agentQuotaLastFetched,
  } = useSelector((state) => state.billing);
  const paymentMethods = rawPaymentMethods || [];
  const invoices = rawInvoices || [];

  const { didTransactions, didTransactionsTotal, transactionsLoading } = useSelector((state) => state.didlogic);

  const [addCardModal, setAddCardModal] = useState(false);
  const [invoicePage, setInvoicePage] = useState(1);
  const [billingTab, setBillingTab] = useState("stripe");
  const [didPage, setDidPage] = useState(1);
  const [didTypeFilter, setDidTypeFilter] = useState("");
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newAgentCount, setNewAgentCount] = useState(null);
  const [agentUpdating, setAgentUpdating] = useState(false);
  const [agentConfirmModal, setAgentConfirmModal] = useState(false);
  const [agentPreview, setAgentPreview] = useState(null);
  const [agentPreviewLoading, setAgentPreviewLoading] = useState(false);
  const [agentCouponInput, setAgentCouponInput] = useState("");
  const [agentCoupon, setAgentCoupon] = useState(null);
  const [agentCouponLoading, setAgentCouponLoading] = useState(false);
  const [agentCouponError, setAgentCouponError] = useState(null);

  useEffect(() => {
    stripePromise.then((stripeInstance) => {
      setStripe(stripeInstance);
      setElements(stripeInstance.elements());
    });
  }, []);

  useEffect(() => {
    const now = Date.now();
    if (!subscriptionLastFetched || now - subscriptionLastFetched > BILLING_TTL) dispatch(fetchCurrentSubscription());
    if (!paymentMethodsLastFetched || now - paymentMethodsLastFetched > BILLING_TTL) dispatch(fetchPaymentMethods());
    if (!agentQuotaLastFetched || now - agentQuotaLastFetched > BILLING_TTL) dispatch(fetchAgentQuota());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 1: preview cost → show confirm modal
  const handlePreviewAgentSeats = async () => {
    if (!newAgentCount || newAgentCount <= (agentQuota?.agentCount || 1)) return;
    setAgentPreviewLoading(true);
    try {
      const result = await dispatch(previewAgentSeats(newAgentCount)).unwrap();
      setAgentPreview(result);
      setAgentConfirmModal(true);
    } catch (err) {
      dispatch(showToast({ message: err || "Failed to preview cost", variant: "danger" }));
    } finally {
      setAgentPreviewLoading(false);
    }
  };

  const handleValidateAgentCoupon = async () => {
    if (!agentCouponInput.trim()) return;
    setAgentCouponLoading(true);
    setAgentCouponError(null);
    try {
      const result = await dispatch(validateCoupon(agentCouponInput.trim().toUpperCase())).unwrap();
      setAgentCoupon(result);
    } catch (err) {
      setAgentCouponError(err || "Invalid coupon");
      setAgentCoupon(null);
    } finally {
      setAgentCouponLoading(false);
    }
  };

  const calcAgentCouponDiscount = (baseAmount) => {
    if (!agentCoupon) return 0;
    if (agentCoupon.discountType === "percentage") return (baseAmount * agentCoupon.discountValue) / 100;
    return agentCoupon.discountValue;
  };

  // Step 2: confirmed → charge
  const handleConfirmAgentSeats = async () => {
    setAgentUpdating(true);
    try {
      const result = await dispatch(updateAgentSeats({ agentCount: newAgentCount, couponCode: agentCoupon?.code })).unwrap();
      if (result.clientSecret && stripe) {
        const { error } = await stripe.confirmCardPayment(result.clientSecret);
        if (error) {
          dispatch(showToast({ message: error.message || "Payment failed", variant: "danger" }));
          setAgentUpdating(false);
          return;
        }
      }
      dispatch(showToast({ message: `Agent seats updated to ${newAgentCount}`, variant: "success" }));
      dispatch(fetchAgentQuota());
      dispatch(fetchInvoices({ page: invoicePage, limit: 10 }));
      setNewAgentCount(null);
      setAgentConfirmModal(false);
      setAgentPreview(null);
      setAgentCoupon(null);
      setAgentCouponInput("");
      setAgentCouponError(null);
    } catch (err) {
      dispatch(showToast({ message: err || "Failed to update seats", variant: "danger" }));
    } finally {
      setAgentUpdating(false);
    }
  };

  useEffect(() => {
    dispatch(fetchInvoices({ page: invoicePage, limit: 10 }));
  }, [invoicePage, dispatch]);

  useEffect(() => {
    if (billingTab === "did") {
      const params = { page: didPage, limit: 15 };
      if (didTypeFilter) params.type = didTypeFilter;
      dispatch(fetchDIDTransactions(params));
    }
  }, [billingTab, didPage, didTypeFilter, dispatch]);

  // Mount/unmount card element when modal opens or closes
  useEffect(() => {
    if (addCardModal && elements && !cardElement) {
      try {
        const instance = elements.create("card", {
          style: {
            base: {
              fontSize: "16px",
              color: "#424770",
              "::placeholder": { color: "#aab7c4" },
            },
            invalid: { color: "#ef4444" },
          },
          hidePostalCode: true,
        });
        const container = document.getElementById("billing-card-element");
        if (container && !container.hasChildNodes()) {
          instance.mount("#billing-card-element");
          instance.on("change", (e) => setCardError(e.error?.message || null));
          setCardElement(instance);
        }
      } catch (err) {
        console.error("Error mounting card element:", err);
      }
    }
    if (!addCardModal && cardElement) {
      try { cardElement.unmount(); } catch (_) {}
      setCardElement(null);
    }
  }, [addCardModal, elements]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddCard = () => setAddCardModal(true);

  const handleSubmitCard = async () => {
    if (!stripe || !cardElement) return;
    setSubmitting(true);
    setCardError(null);
    try {
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });
      if (error) { setCardError(error.message); return; }
      await dispatch(attachPaymentMethod({
        paymentMethodId: paymentMethod.id,
        setAsDefault: paymentMethods.length === 0,
      })).unwrap();
      setAddCardModal(false);
    } catch (err) {
      setCardError(err.message || "Failed to save card");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAddCard = () => {
    setCardError(null);
    setAddCardModal(false);
  };

  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  const statusColor = { trial: "orange", active: "green", paused: "gold", expired: "red", cancelled: "red", none: "default" };
  const statusLabel = { trial: "Free Trial", active: "Active", paused: "Paused", expired: "Expired", cancelled: "Cancelled", none: "No Plan" };

  const invoiceColumns = [
    { title: "Invoice #", dataIndex: "invoiceNumber", render: (v) => v || "—" },
    {
      title: "Description",
      render: (_, r) => {
        // Explicit invoice type set by backend (e.g. "Additional Agent Seats (+2)")
        if (r.invoiceType) {
          return <span style={{ fontWeight: 500 }}>{r.invoiceType}</span>;
        }
        if (r.couponCode === "CREDIT_TOPUP") {
          return <Tag color="blue" style={{ margin: 0 }}>Credit Recharge</Tag>;
        }
        if (r.couponCode === "AUTO_RECHARGE") {
          return <Tag color="cyan" style={{ margin: 0 }}>Auto Recharge</Tag>;
        }
        // Fall back to plan name for regular subscription invoices
        return r.planId?.name || "—";
      },
    },
    {
      title: "Billing Date",
      key: "billingDate",
      render: (_, r) => (
        <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
          {dayjs(r.stripeCreatedAt || r.createdAt).format("MMM D, YYYY")}
        </span>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, r) => {
        // amountPaid = what Stripe actually charged to the card.
        // For seat-addition invoices this is now the exact per-seat price (non-prorated)
        // since we switched to a manual invoice approach. Falls back for older invoices.
        const displayCents = (r.amountPaid != null && r.amountPaid > 0)
          ? r.amountPaid
          : (r.seatFaceAmount ?? r.amount ?? r.planAmount ?? 0);
        return `$${(displayCents / 100).toFixed(2)}`;
      },
    },
    {
      title: "Status", dataIndex: "status",
      render: (s) => (
        <Tag color={s === "paid" ? "green" : s === "open" ? "orange" : "red"}>{s?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {record.hostedInvoiceUrl && (
            <Tooltip title="View Invoice">
              <Button type="text" icon={<EyeOutlined />} href={record.hostedInvoiceUrl} target="_blank" />
            </Tooltip>
          )}
          {record.invoicePdf && (
            <Tooltip title="Download PDF">
              <Button type="text" icon={<DownloadOutlined />} href={record.invoicePdf} target="_blank" />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ margin: 0, fontWeight: 700 }}>Billing & Subscription</h3>
          <span style={{ color: "#6b7280" }}>Manage your plan, payment methods, and invoices</span>
        </div>

        {/* Current Plan Status */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 16, marginBottom: 24 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
            <Statistic
              title="Current Plan"
              value={subscription?.planId?.name || (planStatus === "trial" ? "Free Trial" : "No Active Plan")}
              prefix={<CheckCircleOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Tag color={statusColor[planStatus]}>{statusLabel[planStatus]}</Tag>
            </div>
          </div>

          {planStatus === "trial" && (
            <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
              <Statistic title="Trial Days Left" value={trialDaysLeft} suffix="days" valueStyle={{ color: trialDaysLeft <= 3 ? "#ef4444" : "#f59e0b" }} />
              <span style={{ color: "#6b7280", fontSize: 12 }}>
                Expires {trialEndsAt ? dayjs(trialEndsAt).format("MMM D, YYYY") : "—"}
              </span>
            </div>
          )}

          {subscription && (
            <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
              <Statistic
                title="Billing Period"
                value={subscription.billingPeriod?.charAt(0).toUpperCase() + subscription.billingPeriod?.slice(1)}
              />
              <span style={{ color: "#6b7280", fontSize: 12 }}>
                Renews {subscription.currentPeriodEnd ? dayjs(subscription.currentPeriodEnd).format("MMM D, YYYY") : "—"}
              </span>
              {subscription.cancelAtPeriodEnd && (
                <div><Tag color="orange" style={{ marginTop: 4 }}>Cancels at period end</Tag></div>
              )}
            </div>
          )}

          {subscription && agentQuota && (
            <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
              <Statistic
                title="Agent Seats"
                value={`${agentQuota.usedCount + 1} / ${agentQuota.agentCount}`}
                prefix={<TeamOutlined />}
              />
              <div style={{ marginTop: 4 }}>
                <Tag color={Math.max(0, agentQuota.remaining - 1) === 0 ? "red" : "green"}>
                  {Math.max(0, agentQuota.remaining - 1)} remaining
                </Tag>
              </div>
            </div>
          )}

          <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
            <Statistic title="Total Paid" value={`$${((billingSummary?.totalPaid || 0) / 100).toFixed(2)}`} />
            {(billingSummary?.totalDue || 0) > (billingSummary?.totalPaid || 0) && (
              <div style={{ color: "#f59e0b", fontSize: 12, marginTop: 2 }}>
                ${((billingSummary.totalDue - billingSummary.totalPaid) / 100).toFixed(2)} pending
              </div>
            )}
            <span style={{ color: "#6b7280", fontSize: 12 }}>{billingSummary?.totalBills || 0} invoices</span>
          </div>
        </div>

        {/* Subscription Actions */}
        {subscription && !subscription.cancelAtPeriodEnd && (
          <div style={{ background: "#fff", borderRadius: 12, padding: 20, marginBottom: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
            <Space>
              <span style={{ fontWeight: 600 }}>Subscription Actions</span>
              <Popconfirm
                title="Cancel subscription?"
                description="You'll keep access until the end of the billing period."
                onConfirm={() => dispatch(cancelSubscription())}
                okText="Yes, Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button danger>Cancel Subscription</Button>
              </Popconfirm>
            </Space>
          </div>
        )}

        {/* Agent Seats */}
        {subscription && agentQuota && (
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <TeamOutlined style={{ fontSize: 20, color: "#6366f1" }} />
              <div>
                <h4 style={{ margin: 0, fontWeight: 600 }}>Agent Seats</h4>
                <span style={{ color: "#6b7280", fontSize: 13 }}>
                  {agentQuota.usedCount + 1} of {agentQuota.agentCount} seats used (1 included in plan
                  {agentQuota.agentCount > 1 ? `, ${agentQuota.agentCount - 1} extra` : ""})
                  {agentQuota.agentPrice > 0 && ` · $${agentQuota.agentPrice}/extra agent/mo`}
                </span>
              </div>
            </div>

            <Progress
              percent={Math.min(100, Math.round(((agentQuota.usedCount + 1) / agentQuota.agentCount) * 100))}
              strokeColor={Math.max(0, agentQuota.remaining - 1) === 0 ? "#ef4444" : "#6366f1"}
              style={{ marginBottom: 16 }}
            />

            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{ color: "#374151", fontWeight: 500 }}>Increase to:</span>
              <InputNumber
                min={agentQuota.agentCount + 1}
                value={newAgentCount}
                onChange={(v) => setNewAgentCount(v)}
                placeholder={`More than ${agentQuota.agentCount}`}
                style={{ width: 180 }}
              />
              <Button
                type="primary"
                loading={agentPreviewLoading}
                disabled={!newAgentCount || newAgentCount <= agentQuota.agentCount}
                onClick={handlePreviewAgentSeats}
                style={{ background: "#6366f1", color: "#fff" }}
              >
                Add Seats
              </Button>
              {newAgentCount && newAgentCount > agentQuota.agentCount && agentQuota.agentPrice > 0 && (
                <span style={{ color: "#6b7280", fontSize: 13 }}>
                  +${((newAgentCount - agentQuota.agentCount) * agentQuota.agentPrice).toFixed(2)}/mo additional
                </span>
              )}
            </div>
          </div>
        )}

        {/* Agent Seat Payment Confirmation Modal */}
        <Modal
          title={<span><TeamOutlined style={{ color: "#6366f1", marginRight: 8 }} />Confirm Agent Seat Purchase</span>}
          open={agentConfirmModal}
          onCancel={() => {
            setAgentConfirmModal(false);
            setAgentPreview(null);
            setAgentCoupon(null);
            setAgentCouponInput("");
            setAgentCouponError(null);
          }}
          onOk={handleConfirmAgentSeats}
          confirmLoading={agentUpdating}
          okText="Confirm & Pay"
          okButtonProps={{ style: { background: "#6366f1" } }}
          width={460}
        >
          {agentPreview && (
            <div>
              {/* ── Seat summary ── */}
              <div style={{ background: "#f5f3ff", borderRadius: 10, padding: 16, marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ color: "#6b7280", fontSize: 13 }}>Current seats</span>
                  <span style={{ fontWeight: 600 }}>{agentPreview.currentAgentCount}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ color: "#6b7280", fontSize: 13 }}>New seats</span>
                  <span style={{ fontWeight: 600 }}>{agentPreview.newAgentCount}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ color: "#6b7280", fontSize: 13 }}>Seats being added</span>
                  <span style={{ fontWeight: 600, color: "#6366f1" }}>
                    +{agentPreview.newAgentCount - agentPreview.currentAgentCount} × ${agentPreview.agentPrice?.toFixed(2)}/seat
                  </span>
                </div>

                {/* Full recurring cost — only shown when plan will actually renew */}
                {agentPreview.additionalPerPeriod > 0 && !subscription?.cancelAtPeriodEnd && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ color: "#6b7280", fontSize: 13 }}>
                      Recurring from next {agentPreview.billingPeriod === "monthly" ? "month" : agentPreview.billingPeriod === "quarterly" ? "quarter" : "year"}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>
                      +${agentPreview.additionalPerPeriod?.toFixed(2)}/{agentPreview.billingPeriod === "monthly" ? "mo" : agentPreview.billingPeriod === "quarterly" ? "qtr" : "yr"}
                    </span>
                  </div>
                )}
                {/* Cancellation notice — plan will not renew so no recurring charge applies */}
                {subscription?.cancelAtPeriodEnd && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ color: "#b45309", fontSize: 13, fontStyle: "italic" }}>
                      Plan cancels on {dayjs(subscription.currentPeriodEnd).format("MMM D, YYYY")} — seat active until then
                    </span>
                  </div>
                )}

                <div style={{ borderTop: "1px solid #ddd4fe", marginTop: 4, paddingTop: 12 }}>
                  {/* Proration breakdown — uses exact days returned by backend */}
                  {agentPreview.proratedAmount != null && agentPreview.additionalPerPeriod > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ color: "#6b7280", fontSize: 12 }}>
                        ${agentPreview.additionalPerPeriod.toFixed(2)} × {agentPreview.remainingDays} of {agentPreview.totalDays} days remaining
                      </span>
                      <span style={{ fontSize: 12, color: "#374151" }}>
                        {Math.round((agentPreview.fraction ?? 0) * 100)}%
                      </span>
                    </div>
                  )}

                  {agentCoupon && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ color: "#7c3aed", fontSize: 12 }}>Coupon: {agentCoupon.code}</span>
                      <span style={{ color: "#7c3aed", fontWeight: 600, fontSize: 12 }}>
                        -{agentCoupon.discountType === "percentage"
                          ? `${agentCoupon.discountValue}%`
                          : `$${agentCoupon.discountValue.toFixed(2)}`}
                      </span>
                    </div>
                  )}

                  {/* Prorated total */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Amount to Pay</span>
                    <span style={{ fontWeight: 800, fontSize: 22, color: "#6366f1" }}>
                      {(() => {
                        const base     = agentPreview.proratedAmount != null ? agentPreview.proratedAmount / 100 : (agentPreview.additionalPerPeriod || 0);
                        const discount = calcAgentCouponDiscount(base);
                        return `$${Math.max(0, base - discount).toFixed(2)}`;
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Proration explanation ── */}
              <div style={{
                background: "#fffbeb", border: "1px solid #fde68a",
                borderRadius: 10, padding: "10px 14px", marginBottom: 14,
                fontSize: 12, color: "#78350f", lineHeight: 1.6,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 3 }}>
                  <i className="ti ti-info-circle" style={{ marginRight: 5 }} />
                  Why is this less than ${agentPreview.additionalPerPeriod?.toFixed(2)}?
                </div>
                You have <strong>{agentPreview.remainingDays} day{agentPreview.remainingDays !== 1 ? "s" : ""}</strong> remaining
                out of {agentPreview.totalDays} days in your current billing period. You are only charged for those
                remaining days.
                {subscription?.cancelAtPeriodEnd
                  ? " Since your plan is set to cancel at period end, no recurring charge will follow."
                  : ` From your next renewal, the full $${agentPreview.additionalPerPeriod?.toFixed(2)} will be added automatically.`}
              </div>

              {/* Coupon input */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontWeight: 500, marginBottom: 6, fontSize: 13 }}>Promo Code (optional)</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <Input
                    placeholder="Enter promo code"
                    value={agentCouponInput}
                    onChange={(e) => { setAgentCouponInput(e.target.value.toUpperCase()); setAgentCouponError(null); }}
                    disabled={!!agentCoupon}
                    style={{ textTransform: "uppercase", flex: 1 }}
                    onPressEnter={handleValidateAgentCoupon}
                  />
                  {agentCoupon ? (
                    <Button onClick={() => { setAgentCoupon(null); setAgentCouponInput(""); setAgentCouponError(null); }}>
                      Remove
                    </Button>
                  ) : (
                    <Button loading={agentCouponLoading} onClick={handleValidateAgentCoupon} disabled={!agentCouponInput.trim()}>
                      Apply
                    </Button>
                  )}
                </div>
                {agentCouponError && <div style={{ color: "#ef4444", fontSize: 12, marginTop: 4 }}>{agentCouponError}</div>}
                {agentCoupon && <div style={{ color: "#7c3aed", fontSize: 12, marginTop: 4 }}>✓ Coupon applied</div>}
              </div>

              <p style={{ color: "#9ca3af", fontSize: 12, margin: 0 }}>
                Your default payment method will be charged immediately.
              </p>
            </div>
          )}
        </Modal>

        {/* Payment Methods */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <h4 style={{ margin: 0, fontWeight: 600 }}>Payment Methods</h4>
              <span style={{ color: "#6b7280", fontSize: 13 }}>Manage your saved cards</span>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCard} style={{ background: "#6366f1" }}>
              Add Card
            </Button>
          </div>

          {paymentMethodsLoading ? (
            <div style={{ textAlign: "center", padding: 32 }}>Loading...</div>
          ) : paymentMethods.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: "#9ca3af" }}>
              <CreditCardOutlined style={{ fontSize: 32, marginBottom: 8 }} />
              <p>No payment methods saved yet.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {paymentMethods.map((pm) => {
                const isDefault = pm.id === defaultPaymentMethodId;
                return (
                  <div
                    key={pm.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 16,
                      padding: "14px 20px", borderRadius: 10,
                      border: `2px solid ${isDefault ? "#6366f1" : "#e5e7eb"}`,
                      background: isDefault ? "#f5f3ff" : "#fafafa",
                    }}
                  >
                    <CardBrandIcon brand={pm.card?.brand} />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{pm.card?.brand}</span>
                      {" "}
                      <span style={{ color: "#374151" }}>•••• •••• •••• {pm.card?.last4}</span>
                      <span style={{ color: "#9ca3af", marginLeft: 8, fontSize: 12 }}>
                        Expires {pm.card?.exp_month}/{pm.card?.exp_year}
                      </span>
                    </div>
                    <Space>
                      {isDefault ? (
                        <Tag color="purple" icon={<StarOutlined />}>Default</Tag>
                      ) : (
                        <Button size="small" onClick={() => dispatch(setDefaultPaymentMethod(pm.id))}>
                          Set Default
                        </Button>
                      )}
                      <Popconfirm
                        title="Remove this card?"
                        onConfirm={() => dispatch(removePaymentMethod(pm.id))}
                        okText="Remove"
                        okButtonProps={{ danger: true }}
                        disabled={isDefault}
                      >
                        <Tooltip title={isDefault ? "Cannot remove default card" : "Remove"}>
                          <Button type="text" danger icon={<DeleteOutlined />} disabled={isDefault} />
                        </Tooltip>
                      </Popconfirm>
                    </Space>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Billing History — Stripe + DID tabs */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          <Tabs
            activeKey={billingTab}
            onChange={(k) => { setBillingTab(k); }}
            items={[
              {
                key: "stripe",
                label: (
                  <span>
                    <CreditCardOutlined style={{ marginRight: 6 }} />
                    Subscription Payments
                  </span>
                ),
                children: (
                  <Table
                    columns={invoiceColumns}
                    dataSource={invoices}
                    rowKey="_id"
                    loading={invoicesLoading}
                    pagination={{
                      total: invoicesTotal,
                      pageSize: 10,
                      current: invoicePage,
                      onChange: (page) => setInvoicePage(page),
                      showTotal: (t) => `${t} invoices`,
                    }}
                  />
                ),
              },
              {
                key: "did",
                label: (
                  <span>
                    <PhoneOutlined style={{ marginRight: 6 }} />
                    Phone Number Purchases
                  </span>
                ),
                children: (
                  <div>
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <Select
                        placeholder="All types"
                        allowClear
                        style={{ width: 220 }}
                        value={didTypeFilter || undefined}
                        onChange={(v) => { setDidTypeFilter(v || ""); setDidPage(1); }}
                        options={[
                          { label: "New Phone Number", value: "number_purchase" },
                          { label: "Monthly Fee", value: "monthly_renewal" },
                          { label: "Number Released", value: "number_release_refund" },
                        ]}
                      />
                    </div>
                    <Table
                      dataSource={didTransactions}
                      rowKey="_id"
                      loading={transactionsLoading}
                      pagination={{
                        total: didTransactionsTotal,
                        pageSize: 15,
                        current: didPage,
                        onChange: (p) => setDidPage(p),
                        showTotal: (t) => `${t} transactions`,
                      }}
                      locale={{ emptyText: "No DID transactions yet." }}
                      columns={[
                        {
                          title: "Date",
                          dataIndex: "createdAt",
                          render: (v) => dayjs(v).format("MMM D, YYYY HH:mm"),
                          width: 160,
                        },
                        {
                          title: "Type",
                          dataIndex: "type",
                          render: (v) => {
                            const map = {
                              number_purchase: { label: "New Phone Number", color: "blue" },
                              monthly_renewal: { label: "Monthly Fee", color: "purple" },
                              number_release_refund: { label: "Number Released", color: "green" },
                            };
                            const m = map[v] || { label: v, color: "default" };
                            return <Tag color={m.color}>{m.label}</Tag>;
                          },
                        },
                        {
                          title: "Number",
                          dataIndex: "number",
                          render: (v) => v ? <span className="font-monospace">+{v}</span> : "—",
                        },
                        {
                          title: "Country",
                          dataIndex: "countryName",
                          render: (v) => v || "—",
                        },
                        {
                          title: "Description",
                          dataIndex: "description",
                          render: (v) => v || "—",
                        },
                        {
                          title: "Amount",
                          dataIndex: "amount",
                          align: "right",
                          render: (v, r) => {
                            const isCredit = r.type === "number_release_refund";
                            return (
                              <span className={isCredit ? "text-success fw-semibold" : "text-danger fw-semibold"}>
                                {isCredit ? "+" : "-"}${Number(v || 0).toFixed(2)}
                              </span>
                            );
                          },
                        },
                        {
                          title: "Status",
                          dataIndex: "status",
                          render: (v) => (
                            <Tag color={v === "completed" ? "green" : v === "refunded" ? "blue" : "red"}>
                              {v?.toUpperCase()}
                            </Tag>
                          ),
                        },
                      ]}
                    />
                  </div>
                ),
              },
            ]}
          />
        </div>

        {/* Add Card Modal */}
        <Modal
          title="Add Payment Method"
          open={addCardModal}
          onCancel={handleCloseAddCard}
          onOk={handleSubmitCard}
          confirmLoading={submitting || actionLoading}
          okText="Save Card"
          okButtonProps={{ style: { background: "#6366f1" } }}
        >
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 8, color: "#374151", fontWeight: 500 }}>
              Card Details
            </label>
            <div
              id="billing-card-element"
              style={{
                padding: "12px",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                minHeight: 40,
                backgroundColor: "#fff",
              }}
            />
            {cardError && <Alert message={cardError} type="error" style={{ marginTop: 8 }} showIcon />}
          </div>
          <p style={{ color: "#6b7280", fontSize: 12 }}>
            Your card is stored securely by Stripe. We never store card numbers on our servers.
          </p>
        </Modal>
      </div>
    </div>
  );
}
